import { filter, flat, map, mapValues, omit, pipe } from 'remeda';
import invariant from 'tiny-invariant';

import { Element, Node } from '@@editor/helpers';
import {
    type CustomTextMarks,
    ELEMENT_TYPES,
    type LeadElement,
    type TitleElement,
    type TitleHeaderElement,
} from '@@editor/helpers/Element';
import { createTextNode } from '@@editor/serialization/UnitySerializer/deserializeNodes';
import {
    isUnityText,
    type UnityAuthorLink,
    type UnityCategoryLink,
    type UnityCrosshead,
    type UnityExternalLink,
    type UnityFooter,
    type UnityInternalLink,
    type UnityLead,
    type UnityParagraph,
    type UnityTagLink,
    type UnityText,
    type UnityTextItem,
    type UnityTitle,
    type UnityTitleHeader,
} from '@@editor/typings/UnityElements';
import { cleanObject } from '@@utils/object';

import { CURRENT_VERSION } from './../constants';
import markRules from './markRules';
import { type Next, type TextRule } from '../types';

const getVariants = <T extends TitleHeaderElement | TitleElement | LeadElement>(
    node: T,
    next: Next<T>,
) => node.variants && mapValues(node.variants, (variant) => next(omit(variant, ['id'])));

const textRules: TextRule = {
    deserialize: (element): ReturnType<TextRule['deserialize']> => {
        if (isUnityText(element)) {
            switch (element.type) {
                case ELEMENT_TYPES.TEXT:
                case ELEMENT_TYPES.CROSSHEAD:
                case ELEMENT_TYPES.TITLE_HEADER:
                case ELEMENT_TYPES.TITLE:
                case ELEMENT_TYPES.LEAD:
                case ELEMENT_TYPES.FOOTER:
                    return createTextNode(element, markRules.deserialize);
            }
        }
    },

    serialize: (node, next): ReturnType<TextRule['serialize']> => {
        if (Element.isTextElement(node)) {
            switch (node.type) {
                case ELEMENT_TYPES.PARAGRAPH:
                case ELEMENT_TYPES.CROSSHEAD:
                case ELEMENT_TYPES.TITLE_HEADER:
                case ELEMENT_TYPES.TITLE:
                case ELEMENT_TYPES.LEAD:
                case ELEMENT_TYPES.FOOTER: {
                    invariant(next, 'Cannot serialize text without a `next` function');

                    const items = pipe(
                        node.children,
                        map((childNode) => {
                            if (Element.isInternalLinkElement(childNode)) {
                                return {
                                    type: ELEMENT_TYPES.INTERNAL_LINK,
                                    version: CURRENT_VERSION,
                                    text: Node.string(childNode),
                                    metadataId: childNode.data.metadataId,
                                    ...cleanObject({
                                        attributes: omit(childNode.children[0], ['text']),
                                    }),
                                } satisfies UnityInternalLink;
                            }

                            if (Element.isTagLinkElement(childNode)) {
                                return {
                                    type: ELEMENT_TYPES.TAG_LINK,
                                    version: CURRENT_VERSION,
                                    text: Node.string(childNode),
                                    tagId: childNode.data.tagId,
                                    ...cleanObject({
                                        attributes: omit(childNode.children[0], ['text']),
                                    }),
                                } satisfies UnityTagLink;
                            }

                            if (Element.isCategoryLinkElement(childNode)) {
                                return {
                                    type: ELEMENT_TYPES.CATEGORY_LINK,
                                    version: CURRENT_VERSION,
                                    text: Node.string(childNode),
                                    categoryId: childNode.data.categoryId,
                                    ...cleanObject({
                                        attributes: omit(childNode.children[0], ['text']),
                                    }),
                                } satisfies UnityCategoryLink;
                            }

                            if (Element.isAuthorLinkElement(childNode)) {
                                return {
                                    type: ELEMENT_TYPES.AUTHOR_LINK,
                                    version: CURRENT_VERSION,
                                    text: Node.string(childNode),
                                    authorId: childNode.data.authorId,
                                    ...cleanObject({
                                        attributes: omit(childNode.children[0], ['text']),
                                    }),
                                } satisfies UnityAuthorLink;
                            }

                            if (Element.isExternalLinkElement(childNode)) {
                                return {
                                    type: ELEMENT_TYPES.EXTERNAL_LINK,
                                    version: CURRENT_VERSION,
                                    text: Node.string(childNode),
                                    url: childNode.data.href,
                                    ...cleanObject({
                                        attributes: omit(childNode.children[0], ['text']),
                                    }),
                                } satisfies UnityExternalLink;
                            }

                            return {
                                version: CURRENT_VERSION,
                                type: ELEMENT_TYPES.TEXTITEM,
                                text: childNode.text,
                                ...cleanObject({
                                    attributes: next(omit(childNode, ['text']) as CustomTextMarks),
                                }),
                            } satisfies UnityTextItem;
                        }),
                        flat(),
                        filter((node) => Boolean(node.text)),
                    );

                    const baseData = {
                        version: CURRENT_VERSION,
                        items,
                    } satisfies Omit<UnityText, 'type'>;

                    switch (node.type) {
                        case ELEMENT_TYPES.PARAGRAPH:
                            return {
                                ...baseData,
                                ...(node.data || {}),
                                type: ELEMENT_TYPES.TEXT,
                            } satisfies UnityParagraph;

                        case ELEMENT_TYPES.CROSSHEAD:
                            return {
                                ...baseData,
                                ...(node.data || {}),
                                type: node.type,
                            } satisfies UnityCrosshead;

                        case ELEMENT_TYPES.TITLE_HEADER:
                            return {
                                ...baseData,
                                ...(node.data || {}),
                                type: node.type,
                                variants: getVariants(node, next),
                            } satisfies UnityTitleHeader;

                        case ELEMENT_TYPES.TITLE:
                            return {
                                ...baseData,
                                ...(node.data || {}),
                                type: node.type,
                                variants: getVariants(node, next),
                            } satisfies UnityTitle;

                        case ELEMENT_TYPES.LEAD:
                            return {
                                ...baseData,
                                ...(node.data || {}),
                                type: node.type,
                                variants: getVariants(node, next),
                            } satisfies UnityLead;

                        case ELEMENT_TYPES.FOOTER:
                            return {
                                ...baseData,
                                ...(node.data || {}),
                                type: node.type,
                            } satisfies UnityFooter;
                    }
                }
            }
        }
    },
};

export default textRules;
